import { gql } from "@apollo/client";
import { Classes } from "@blueprintjs/core";
import {
  DateTimeString,
  ExploreId,
  HexFilter,
  HexId,
  HexOrder,
  HexType,
  SortDirection,
  isCustomExploreTitle,
} from "@hex/common";
import { format } from "date-fns";
import { sortBy } from "lodash";
import React, { useCallback, useMemo, useState } from "react";

import { HexTooltip } from "../../hex-components";
import { ClientCounterEvent } from "../../util/clientCounterEvent";
import { evtModKey } from "../../util/Keys";
import { useProjectContext } from "../../util/projectContext";
import { trackCounterEvent } from "../../util/trackEvent";
import { useRecentThreads } from "../agent/ask-from-home/hooks/useRecentThreads";
import { openExplore } from "../home/explorations-tab/explorationsTabUtils";
import { useGetExploresQuery } from "../home/explorations-tab/table/useExploresList.generated";
import { SmallRowNavItem } from "../home/SmallRowNavItem";
import { ExploreIcon } from "../icons/CustomIcons";
import { DEFAULT_THREAD_TITLE } from "../logic/shared";

import { SidebarSection } from "./favorite-projects-and-collection-rows/SidebarSection";
import { FixedHeightMenuItem } from "./favorite-projects-and-collection-rows/styled";
import { useGetHexesForRecentSidebarItemsQuery } from "./RecentItems.generated";

gql`
  query GetHexesForRecentSidebarItems(
    $onlyViewed: Boolean
    $order: HexOrder
    $first: Int
  ) {
    hexes(
      ownershipLevel: ALL
      onlyPublished: false
      onlyStarred: false
      onlyViewed: $onlyViewed
      order: $order
      trashed: false
      first: $first
      hexTypes: [PROJECT, COMPONENT]
    ) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          lastViewedByMe
          ...HexListFragment
        }
      }
    }
  }
`;

function getExploreListTitle(
  explore?: {
    isSaved: boolean;
    title?: string | null;
    createdDate: string;
  } | null,
) {
  if (!explore) return null;
  return explore.title &&
    (explore.isSaved || isCustomExploreTitle(explore.title))
    ? explore.title
    : "Exploration on " + format(new Date(explore.createdDate), "MMM d, p");
}
interface ExploreRowNavItemProps {
  exploreId: ExploreId;
  hexId: HexId;
  isSaved: boolean;
  title?: string;
  createdDate: DateTimeString;
  counterEventName?: ClientCounterEvent;
}

const ExploreRowNavItem: React.ComponentType<ExploreRowNavItemProps> =
  React.memo(function ExploreRowNavItem(explore) {
    const title = getExploreListTitle(explore);
    const [ellipsisVisible, setEllipsisVisible] = useState(false);

    const nameRef = useCallback((el: HTMLDivElement) => {
      setEllipsisVisible(el ? el.offsetWidth < el.scrollWidth : false);
    }, []);

    const onClick = useCallback(
      (evt: React.MouseEvent) => {
        openExplore(explore.hexId, explore.exploreId, evtModKey(evt));
        if (explore.counterEventName) {
          trackCounterEvent(explore.counterEventName);
        }
      },
      [explore.counterEventName, explore.exploreId, explore.hexId],
    );

    return (
      <HexTooltip
        content={title}
        disabled={!ellipsisVisible}
        hoverOpenDelay={300}
        position="right"
        targetTagName="div"
      >
        <FixedHeightMenuItem
          icon={<ExploreIcon />}
          text={
            <div ref={nameRef} className={Classes.TEXT_OVERFLOW_ELLIPSIS}>
              {title}
            </div>
          }
          onClick={onClick}
        />
      </HexTooltip>
    );
  });

export const RecentItems = React.memo(function RecentItems({
  hideIfEmpty,
  location,
}: {
  hideIfEmpty?: boolean;
  location: "popover_sidebar" | "home_sidebar";
}) {
  const projectContext = useProjectContext({ safe: true });
  const currentHexId = projectContext?.hexId;
  const {
    data: recentExploresData,
    error: recentExploresError,
    loading: recentExploresLoading,
  } = useGetExploresQuery({
    variables: {
      sortField: "CREATED_DATE" as const,
      sortDirection: SortDirection.DESC,
      saved: HexFilter.INCLUDE,
      after: null,
      before: null,
      last: null,
      first: 10,
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const {
    data: recentHexesData,
    error: recentError,
    loading: recentLoading,
  } = useGetHexesForRecentSidebarItemsQuery({
    variables: {
      order: HexOrder.RECENTLY_VIEWED,
      onlyViewed: true,
      first: 10,
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const {
    error: threadsError,
    loading: threadsLoading,
    threads,
  } = useRecentThreads(10);
  const recentItems = useMemo(() => {
    const hexes =
      recentHexesData?.hexes.edges
        .map(({ node }) => ({
          ...node,
          type: "hex" as const,
          dateToSortOn: node.lastViewedByMe,
        }))
        .filter(({ id }) => id !== currentHexId) ?? [];
    const explores =
      recentExploresData?.exploresV2.edges
        .map(({ node }) => ({
          ...node,
          type: "explore" as const,
          dateToSortOn: node.createdDate,
        }))
        .filter((explore) => explore.__typename === "Explore") ?? [];
    const threadsWithTypename = threads.map((thread) => ({
      ...thread,
      type: "thread" as const,
      dateToSortOn: thread.lastUpdated,
    }));
    const allItems = [...hexes, ...explores, ...threadsWithTypename];
    const sortedItems = sortBy(allItems, "dateToSortOn").reverse();

    return sortedItems.map((item) =>
      item.type === "hex" ? (
        <SmallRowNavItem
          key={item.id}
          canEdit={item.canEdit}
          canViewLogic={item.canViewLogic}
          counterEventName={`${location}.recents.hex.click`}
          id={item.id}
          isPublished={item.lastPublishedVersion != null}
          isStarred={item.starredByViewer}
          title={item.title}
          type={item.hexType}
        />
      ) : item.type === "explore" ? (
        <ExploreRowNavItem
          key={item.id}
          counterEventName={`${location}.recents.explore.click`}
          createdDate={item.createdDate}
          exploreId={item.id}
          hexId={item.hexVersion.hexId}
          isSaved={item.saved}
          title={item.hexVersion.title}
        />
      ) : (
        <SmallRowNavItem
          key={item.id}
          canEdit={false}
          canViewLogic={false}
          counterEventName={`${location}.recents.threads.click`}
          id={item.id}
          isPublished={false}
          isStarred={false}
          title={item.title ?? DEFAULT_THREAD_TITLE}
          type={HexType.ASK}
        />
      ),
    );
  }, [
    recentHexesData?.hexes.edges,
    recentExploresData?.exploresV2.edges,
    threads,
    currentHexId,
    location,
  ]);

  const anyLoading = recentLoading || recentExploresLoading || threadsLoading;
  const anyError = recentError || recentExploresError || threadsError;

  return (
    <SidebarSection
      error={anyError}
      hideIfEmpty={hideIfEmpty}
      items={recentItems}
      loading={anyLoading}
      localStorageKey="SIDEBAR_RECENT_ITEMS_OPENED"
      text="Recents"
    />
  );
});
